import { ApdmCampaignKind, ApdmCampaignStatus } from './campaign';
import { RpaMessageType } from './campaign-review';
import { CouponCampaignPublicViewItem } from './coupon-redemption';

export interface ApdmReportRequest {
  campaignId: string;
}
export interface ApdmReportMetrics {
  audience: number;
  reached: number;
  unconfirmed: number;
  unreachable: number;
  replied: number;
  noResponse: number;
  doNotContact: number;
  engaged: number;
  engagedElsewhere: number;
  booked: number;
  converted: number;
  convertedReached: number;
  lost: number;
  redeemed: number;
  replyRate: number | null;
  conversionRate: number | null;
}
export interface ApdmReportBreakdown {
  key: string;
  label: string;
  metrics: ApdmReportMetrics;
}
export interface ApdmAdvisorReport extends ApdmReportBreakdown {
  draftsReviewed: number;
  averageReviewSeconds: number | null;
}
export interface ApdmCampaignReport {
  campaignId: string;
  title: string;
  kind: ApdmCampaignKind;
  status: ApdmCampaignStatus;
  scope: 'full' | 'assigned';
  funnel: ApdmReportMetrics;
  variants: ApdmReportBreakdown[];
  advisors: ApdmAdvisorReport[];
  days: ApdmReportBreakdown[];
  sources: ApdmReportBreakdown[];
  replyThemes: Array<{ intent: string; label: string; count: number }>;
  timing: { firstReachedAt: string | null; lastReachedAt: string | null };
}
/** The three things an owner reads a campaign summary for. */
export enum ApdmSummarySectionKind {
  Outcome = 'outcome',
  Effect = 'effect',
  /** Where it is heading by its end date, or the takeaway once ended. */
  Outlook = 'outlook',
}
export interface ApdmSummarySection {
  kind: ApdmSummarySectionKind;
  text: string;
}
/** Once a campaign has ended its forecast line is the next-time takeaway. */
export function apdmSummarySectionLabel(
  kind: ApdmSummarySectionKind,
  ended: boolean,
): string {
  if (kind === ApdmSummarySectionKind.Outcome) return '结果';
  if (kind === ApdmSummarySectionKind.Effect) return '效果';
  return ended ? '下次' : '预计';
}
export interface ApdmCampaignSummaryResult {
  campaignId: string;
  factsHash: string;
  sections: ApdmSummarySection[];
  generatedAt: string;
  cached: boolean;
}
export interface ApdmPastCampaignInsightsRequest {
  limit: number;
}
export interface ApdmPastCampaignInsight {
  campaignId: string;
  title: string;
  kind: ApdmCampaignKind;
  summary: ApdmCampaignSummaryResult | null;
}
export interface ApdmPastCampaignInsightsResult {
  campaigns: ApdmPastCampaignInsight[];
}

/** One step of a streamed summary. */
export type ApdmSummaryStreamEvent =
  | { event: 'narration'; data: { text: string } }
  | { event: 'token'; data: { text: string } }
  | { event: 'done'; data: ApdmCampaignSummaryResult }
  | {
      event: 'error';
      data: { code: string; subCode?: string; message: string };
    };

export interface ApdmRecipientReportRequest extends ApdmReportRequest {
  recipientId: string;
}
export interface ApdmRecipientReportMessage {
  id: string;
  sender: string;
  /** Absent from servers before images were shown. */
  type?: RpaMessageType;
  /** Empty for anything but text. */
  text: string;
  /** A short-lived link to an image the shop sent; null when it is gone. */
  imageUrl?: string | null;
  /** The customer's coupon poster: shown as `couponCard`, not the image. */
  couponPoster?: boolean;
  sentAt: string;
}
/** The coupon a campaign sent, as the customer's poster shows it. */
export interface ApdmRecipientCouponCard {
  title: string;
  storeName: string;
  startsAt: string;
  endsAt: string;
  items: CouponCampaignPublicViewItem[];
  /** The QR the customer was sent; null when the recipient has none. */
  qrUrl: string | null;
}
export interface ApdmRecipientReportContext {
  recipientId: string;
  messages: ApdmRecipientReportMessage[];
  /** Absent from servers before the coupon card was shown. */
  couponCard?: ApdmRecipientCouponCard | null;
  coupons: Array<{
    id: string;
    label: string;
    status: string;
    redeemedAt: string | null;
  }>;
}
